import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { useSettings } from '@/context/SettingsContext';
import { Maximize2, Eye, Users, Waves, X, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const RoomsPage = () => {
  const { currentLanguage, t } = useLanguage();
  const { settings } = useSettings();

  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeGallery, setActiveGallery] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const response = await axios.get(`${API}/rooms`);
        setRooms(response.data);
      } catch (error) {
        console.error('Error fetching rooms:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRooms();
  }, []);

  const openGallery = (images) => {
    setActiveGallery(images);
    setActiveImageIndex(0);
  };

  const closeGallery = () => {
    setActiveGallery(null);
  };

  const nextImage = (e) => {
    e.stopPropagation();
    if (!activeGallery) return;
    setActiveImageIndex((prev) => (prev + 1) % activeGallery.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    if (!activeGallery) return;
    setActiveImageIndex((prev) => (prev - 1 + activeGallery.length) % activeGallery.length);
  };

  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center bg-gray-50">
        <div className="w-12 h-12 border-4 border-holiday-purple border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="rooms-page">
      {/* Hero */}
      <section
        className="relative h-[60vh] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `url('/images/rooms/premium_sea.jpg')`
        }}
      >
        <div className="absolute inset-0 bg-holiday-purple/60" />
        <div className="relative z-10 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-heading font-bold mb-4">{t('rooms.title')}</h1>
          <p className="text-xl text-white/80">{t('rooms.subtitle')}</p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 bg-white">
        <div className="container-custom text-center max-w-3xl mx-auto">
          <p className="text-lg text-gray-600 leading-relaxed">
            {t('rooms.description')}
          </p>
        </div>
      </section>

      {/* Rooms Grid */}
      <section className="section-padding bg-gray-50" data-testid="rooms-grid">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {rooms.map((room, index) => {
              const title = room.title?.[currentLanguage] || room.title?.['tr'] || '';
              const subtitle = room.subtitle?.[currentLanguage] || room.subtitle?.['tr'] || '';
              const size = room.size?.[currentLanguage] || room.size?.['tr'] || '';
              const description = room.description?.[currentLanguage] || room.description?.['tr'] || '';
              const views = room.views?.[currentLanguage] || room.views?.['tr'] || [];
              const features = room.features?.[currentLanguage] || room.features?.['tr'] || [];

              return (
                <div
                  key={room.id || index}
                  className={`group bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 ${
                    room.highlight ? 'ring-2 ring-holiday-green' : ''
                  }`}
                  data-testid={`room-${index}`}
                >
                  <div 
                    className={`relative aspect-[16/10] overflow-hidden ${room.images && room.images.length > 1 ? 'cursor-pointer' : ''}`}
                    onClick={() => room.images && room.images.length > 1 && openGallery(room.images)}
                  >
                    <img
                      src={room.image}
                      alt={title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    {room.images && room.images.length > 1 && (
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                        <div className="bg-white/90 text-holiday-purple text-sm font-semibold px-4 py-2 rounded-full flex items-center gap-2 shadow-lg transform translate-y-4 group-hover:translate-y-0 transition-all duration-300">
                          <Eye className="w-4 h-4" />
                          <span>Fotoğrafları Gör ({room.images.length})</span>
                        </div>
                      </div>
                    )}
                    {room.highlight && (
                      <div className="absolute top-4 right-4 bg-holiday-green text-white px-4 py-1 rounded-full text-sm font-medium z-10">
                        Premium
                      </div>
                    )}
                  </div>
                  <div className="p-8">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-2xl font-heading font-bold text-gray-900">{title}</h3>
                        <p className="text-holiday-purple font-medium">{subtitle}</p>
                      </div>
                      <div className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full">
                        <Maximize2 className="w-4 h-4 text-gray-500" />
                        <span className="text-sm font-medium text-gray-700">{size}</span>
                      </div>
                    </div>
                    
                    <p className="text-gray-600 mb-6">{description}</p>
                    
                    {views.length > 0 && (
                      <div className="flex flex-wrap gap-4 mb-6">
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Eye className="w-4 h-4" />
                          <span>{views.join(', ')}</span>
                        </div>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2">
                      {features.map((feature, idx) => (
                        <span
                          key={idx}
                          className="text-xs bg-holiday-purple/10 text-holiday-purple px-3 py-1 rounded-full"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-holiday-purple text-white text-center">
        <div className="container-custom">
          <h2 className="text-4xl font-heading font-bold mb-4">
            {t('hero.cta')}
          </h2>
          <p className="text-white/80 mb-8">{t('hero.concept')}</p>
          <a
            href={settings.reservation_url || "https://www.ihg.com"}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-accent inline-block rounded-sm"
            data-testid="rooms-cta"
          >
            {t('nav.bookNow')}
          </a>
        </div>
      </section>

      {/* Lightbox Gallery Modal */}
      {activeGallery && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 md:p-8 select-none"
          onClick={closeGallery}
        >
          {/* Close button */}
          <button 
            className="absolute top-6 right-6 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 p-2 rounded-full transition-all"
            onClick={closeGallery}
          >
            <X className="w-6 h-6" />
          </button>

          {/* Left Arrow */}
          {activeGallery.length > 1 && (
            <button 
              className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 p-3 rounded-full transition-all"
              onClick={prevImage}
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
          )}

          {/* Active Image */}
          <div className="relative max-w-full max-h-[85vh] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <img 
              src={activeGallery[activeImageIndex]} 
              alt="Room view" 
              className="max-w-full max-h-[80vh] object-contain rounded shadow-2xl transition-all duration-300"
            />
            {/* Image Count Indicator */}
            {activeGallery.length > 1 && (
              <div className="absolute bottom-[-40px] left-1/2 -translate-x-1/2 text-white/80 text-sm font-medium">
                {activeImageIndex + 1} / {activeGallery.length}
              </div>
            )}
          </div>

          {/* Right Arrow */}
          {activeGallery.length > 1 && (
            <button 
              className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 p-3 rounded-full transition-all"
              onClick={nextImage}
            >
              <ChevronRight className="w-8 h-8" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default RoomsPage;
